import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Checkbox,
  CheckboxGroup,
  Text,
  Badge,
  IconButton,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
} from '@chakra-ui/react';
import { Key, MagnifyingGlass, ChartLine } from 'phosphor-react';
import { useActionToast } from '../../services/actionToast';
import { PageTitle } from '../../components/PageTitle';
import { DataSkeleton, EmptyState, ErrorState } from '../../components/states';
import { isRequestFailure, useFetch } from '../../hooks/useFetch';
import { api } from '../../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  group: {
    id: number;
    name: string;
  };
  status: boolean;
}

interface Group {
  id: number;
  name: string;
}

interface Pagination {
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

interface UserAnalytics {
  last_login: string | null;
  last_activity: string | null;
  login_count: number;
  session_duration: number | null;
  total_sessions: number;
}

const UserAccessManagementPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSecretary, setSelectedSecretary] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissionMode, setPermissionMode] = useState<'reuse' | 'create'>('reuse');
  const [selectedHierarchy, setSelectedHierarchy] = useState('');
  const [newHierarchyName, setNewHierarchyName] = useState('');
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [hierarchies, setHierarchies] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAnalyticsOpen,
    onOpen: onAnalyticsOpen,
    onClose: onAnalyticsClose,
  } = useDisclosure();
  const toast = useActionToast();

  const {
    requestApi: fetchUsers,
    data: usersData,
    loading: loadingUsers,
    error: usersError,
  } = useFetch('/v1/admin/users');

  const { requestApi: fetchGroups, data: groupsData } = useFetch('/v1/admin/groups');

  const { requestApi: fetchSecretaries, data: secretariesData } = useFetch('/v1/admin/secretaries');

  const { requestApi: fetchInstitutions, data: institutionsData } =
    useFetch('/v1/admin/institutions');

  const { requestApi: fetchHierarchies } = useFetch('/v1/admin/permissions/resources');

  const { requestApi: saveUserPermissions, loading: savingPermissions } = useFetch(
    '/v1/admin/permissions/users/manage-bulk',
    { actionFeedback: false }
  );

  const loadUsers = async () => {
    const params: any = { page: currentPage, per_page: perPage };
    if (searchTerm) params.search = searchTerm;
    if (selectedGroup) params.group_id = parseInt(selectedGroup, 10);
    if (selectedSecretary) params.secretary_id = parseInt(selectedSecretary, 10);
    if (selectedInstitution) params.institution_id = parseInt(selectedInstitution, 10);

    await fetchUsers('get', params);
  };

  const loadGroups = async () => {
    await fetchGroups('get', { page: 1, per_page: 100 });
  };

  const loadSecretaries = async () => {
    await fetchSecretaries('get', { page: 1, per_page: 100 });
  };

  const loadInstitutions = async () => {
    const params: any = { page: 1, per_page: 100 };
    if (selectedSecretary) params.secretary_id = selectedSecretary;
    await fetchInstitutions('get', params);
  };

  const loadHierarchies = async () => {
    try {
      const response = await fetchHierarchies('get');

      // O backend retorna no formato: { data: [...], pagination: {...} }
      if (response?.data) {
        const resources = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setHierarchies(resources);
      } else {
        setHierarchies([]);
      }
    } catch (error) {
      setHierarchies([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadGroups();
    loadSecretaries();
    loadInstitutions();
    loadHierarchies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedGroup, selectedSecretary, selectedInstitution, currentPage, perPage]);

  useEffect(() => {
    if (selectedSecretary) {
      setSelectedInstitution('');
      loadInstitutions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSecretary]);

  const handleEditPermissions = (user: User) => {
    setSelectedUser(user);
    setPermissionMode('reuse');
    setSelectedHierarchy('');
    setNewHierarchyName('');
    setSelectedActions([]);
    onOpen();
  };

  const handleViewAnalytics = async (user: User) => {
    setSelectedUser(user);
    setLoadingAnalytics(true);
    onAnalyticsOpen();

    try {
      const response = await api.get(`/v1/admin/users/${user.id}`);
      const userData = response.data?.data;

      setUserAnalytics({
        last_login: userData?.last_login || null,
        last_activity: userData?.last_activity || null,
        login_count: userData?.login_count || 0,
        session_duration: userData?.session_duration || null,
        total_sessions: userData?.login_count || 0,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados de analytics do usuário',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onAnalyticsClose();
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    try {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds || seconds === 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    // Validações
    if (permissionMode === 'reuse' && !selectedHierarchy) {
      toast({
        title: 'Erro',
        description: 'Selecione uma hierarquia existente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (permissionMode === 'create') {
      if (!newHierarchyName.trim()) {
        toast({
          title: 'Erro',
          description: 'Informe o nome da nova hierarquia',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (hierarchies.includes(newHierarchyName.trim())) {
        toast({
          title: 'Erro',
          description: 'Já existe uma hierarquia com este nome',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (selectedActions.length === 0) {
        toast({
          title: 'Erro',
          description: 'Selecione pelo menos uma ação',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      const payload = {
        user_id: selectedUser.id,
        mode: permissionMode,
        hierarchy: permissionMode === 'reuse' ? selectedHierarchy : newHierarchyName,
        actions: permissionMode === 'create' ? selectedActions : [],
      };

      const result = await saveUserPermissions('post', payload);
      if (isRequestFailure(result)) throw result.error;

      toast({
        title: 'Sucesso',
        description: 'Permissões atualizadas com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
      loadUsers();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao salvar permissões',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const users = usersData?.data || [];
  const paginationData = usersData?.pagination;
  const pagination: Pagination = {
    total: paginationData?.total_items || 0,
    page: paginationData?.current_page || 1,
    per_page: perPage,
    pages: paginationData?.total_pages || 1,
  };

  const handlePerPageChange = (value: string) => {
    setPerPage(parseInt(value, 10));
    setCurrentPage(1); // Reset to first page when changing perPage
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pagination.pages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <Box>
      <PageTitle>Gerenciar Acessos de Usuários</PageTitle>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Gerencie permissões e acessos dos usuários do sistema
      </Text>

      {/* Filtros */}
      <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={4}>
        <Flex gap={4} flexDir={{ base: 'column', md: 'row' }} mb={4}>
          <FormControl flex={1}>
            <FormLabel fontSize="sm">Buscar usuário</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass size={20} />
              </InputLeftElement>
              <Input
                placeholder="Nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel fontSize="sm">Filtrar por grupo</FormLabel>
            <Select
              placeholder="Todos os grupos"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
            >
              {Array.isArray(groupsData?.data) &&
                groupsData?.data.map((group: Group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
            </Select>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel fontSize="sm">Filtrar por secretaria</FormLabel>
            <Select
              placeholder="Todas as secretarias"
              value={selectedSecretary}
              onChange={(e) => setSelectedSecretary(e.target.value)}
            >
              {Array.isArray(secretariesData?.data) &&
                secretariesData?.data.map((secretary: any) => (
                  <option key={secretary.id} value={secretary.id}>
                    {secretary.name}
                  </option>
                ))}
            </Select>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel fontSize="sm">Filtrar por instituição</FormLabel>
            <Select
              placeholder="Todas as instituições"
              value={selectedInstitution}
              onChange={(e) => setSelectedInstitution(e.target.value)}
              isDisabled={!selectedSecretary}
            >
              {Array.isArray(institutionsData?.data) &&
                institutionsData?.data.map((institution: any) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
            </Select>
          </FormControl>

          <FormControl width={{ base: '100%', md: '150px' }}>
            <FormLabel fontSize="sm">Itens por página</FormLabel>
            <Select value={perPage} onChange={(e) => handlePerPageChange(e.target.value)}>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </Select>
          </FormControl>
        </Flex>
      </Box>

      {/* Tabela de Usuários */}
      <Box bg="white" borderRadius="md" shadow="sm" p={4}>
        {(loadingUsers || (!usersData && !usersError)) && users.length === 0 && (
          <DataSkeleton variant="table" rows={8} columns={5} label="Carregando usuários" />
        )}
        {!loadingUsers && usersError && users.length === 0 && <ErrorState onRetry={loadUsers} />}
        {!loadingUsers && !usersError && usersData && users.length === 0 && (
          <EmptyState
            title="Nenhum usuário encontrado"
            description="Altere os filtros para localizar outros usuários."
            action={{
              label: 'Limpar filtros',
              onClick: () => {
                setSearchTerm('');
                setSelectedGroup('');
                setSelectedSecretary('');
                setSelectedInstitution('');
              },
            }}
          />
        )}
        {users.length > 0 && (
          <>
            <TableContainer>
              <Table variant="striped" colorScheme="gray" size="sm">
                <Thead>
                  <Tr>
                    <Th>Nome</Th>
                    <Th>Email</Th>
                    <Th>Grupo</Th>
                    <Th>Status</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((user: User) => (
                    <Tr key={user.id}>
                      <Td>{user.name}</Td>
                      <Td>{user.email}</Td>
                      <Td>
                        <Badge colorScheme="blue" fontSize="sm">
                          {user.group?.name || 'Sem grupo'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={user.status ? 'green' : 'red'} fontSize="sm">
                          {user.status ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </Td>
                      <Td>
                        <Flex gap={1}>
                          <Tooltip label="Ver Analytics">
                            <IconButton
                              aria-label="Ver analytics do usuário"
                              icon={<ChartLine size={20} />}
                              size="sm"
                              colorScheme="purple"
                              variant="ghost"
                              onClick={() => handleViewAnalytics(user)}
                            />
                          </Tooltip>
                          <Tooltip label="Editar Permissões">
                            <IconButton
                              aria-label="Editar permissões"
                              icon={<Key size={20} />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEditPermissions(user)}
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Flex
              mt={4}
              justify="space-between"
              align="center"
              flexDir={{ base: 'column', md: 'row' }}
              gap={4}
            >
              <Text fontSize="sm" color="gray.600">
                Mostrando {(currentPage - 1) * perPage + 1} a{' '}
                {Math.min(currentPage * perPage, pagination.total)} de {pagination.total} resultados
              </Text>

              <Flex gap={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePreviousPage}
                  isDisabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <Button size="sm" variant="outline" isDisabled>
                  Página {currentPage} de {pagination.pages}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleNextPage}
                  isDisabled={currentPage >= pagination.pages}
                >
                  Próxima
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </Box>

      {/* Modal de Edição de Permissões */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Permissões - {selectedUser?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Como deseja configurar as permissões?</FormLabel>
                <RadioGroup value={permissionMode} onChange={(val: any) => setPermissionMode(val)}>
                  <Stack>
                    <Radio value="reuse">
                      <Text fontWeight="medium">Reaproveitar hierarquia existente</Text>
                      <Text fontSize="sm" color="gray.600">
                        Aplicar um conjunto de permissões já configurado
                      </Text>
                    </Radio>
                    <Radio value="create">
                      <Text fontWeight="medium">Criar nova hierarquia personalizada</Text>
                      <Text fontSize="sm" color="gray.600">
                        Definir permissões específicas para este usuário
                      </Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {permissionMode === 'reuse' && (
                <FormControl>
                  <FormLabel>Selecionar hierarquia</FormLabel>
                  <Select
                    placeholder="Escolha uma hierarquia..."
                    value={selectedHierarchy}
                    onChange={(e) => setSelectedHierarchy(e.target.value)}
                  >
                    {hierarchies.map((hierarchy) => (
                      <option key={hierarchy} value={hierarchy}>
                        {hierarchy}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {permissionMode === 'create' && (
                <>
                  <FormControl>
                    <FormLabel>Nome da nova hierarquia</FormLabel>
                    <Input
                      placeholder="Ex: Coordenadores, Supervisores..."
                      value={newHierarchyName}
                      onChange={(e) => setNewHierarchyName(e.target.value)}
                    />
                    {newHierarchyName && hierarchies.includes(newHierarchyName.trim()) && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        ⚠️ Já existe uma hierarquia com este nome
                      </Text>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Ações permitidas</FormLabel>
                    <CheckboxGroup
                      value={selectedActions}
                      onChange={(val: any) => setSelectedActions(val)}
                    >
                      <Stack spacing={2}>
                        <Checkbox value="create">
                          <Text fontWeight="medium">Criar (create)</Text>
                          <Text fontSize="sm" color="gray.600">
                            Permite criar novos registros
                          </Text>
                        </Checkbox>
                        <Checkbox value="read">
                          <Text fontWeight="medium">Visualizar (read)</Text>
                          <Text fontSize="sm" color="gray.600">
                            Permite visualizar registros
                          </Text>
                        </Checkbox>
                        <Checkbox value="update">
                          <Text fontWeight="medium">Editar (update)</Text>
                          <Text fontSize="sm" color="gray.600">
                            Permite editar registros existentes
                          </Text>
                        </Checkbox>
                        <Checkbox value="delete">
                          <Text fontWeight="medium">Excluir (delete)</Text>
                          <Text fontSize="sm" color="gray.600">
                            Permite excluir registros
                          </Text>
                        </Checkbox>
                      </Stack>
                    </CheckboxGroup>
                  </FormControl>
                </>
              )}
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSavePermissions}
              isLoading={savingPermissions}
            >
              Salvar Permissões
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Analytics do Usuário */}
      <Modal isOpen={isAnalyticsOpen} onClose={onAnalyticsClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Analytics do Usuário - {selectedUser?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingAnalytics ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" color="purple.500" />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Box
                  p={4}
                  bg="purple.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="purple.200"
                >
                  <Text fontSize="sm" fontWeight="600" color="purple.800" mb={2}>
                    Informações de Acesso
                  </Text>
                  <Stack spacing={3}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Último login:
                      </Text>
                      <Text fontSize="sm" fontWeight="600">
                        {formatDateTime(userAnalytics?.last_login || null)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Última atividade:
                      </Text>
                      <Text fontSize="sm" fontWeight="600">
                        {formatDateTime(userAnalytics?.last_activity || null)}
                      </Text>
                    </Flex>
                  </Stack>
                </Box>

                <Box p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                  <Text fontSize="sm" fontWeight="600" color="blue.800" mb={2}>
                    Estatísticas de Uso
                  </Text>
                  <Stack spacing={3}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Total de logins:
                      </Text>
                      <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                        {userAnalytics?.login_count || 0}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Duração média da sessão:
                      </Text>
                      <Text fontSize="sm" fontWeight="600">
                        {formatDuration(userAnalytics?.session_duration || null)}
                      </Text>
                    </Flex>
                  </Stack>
                </Box>

                <Box p={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="600" color="gray.800" mb={2}>
                    Informações do Usuário
                  </Text>
                  <Stack spacing={3}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Email:
                      </Text>
                      <Text fontSize="sm" fontWeight="600">
                        {selectedUser?.email}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Grupo:
                      </Text>
                      <Badge colorScheme="blue" fontSize="sm">
                        {selectedUser?.group?.name || 'Sem grupo'}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Status:
                      </Text>
                      <Badge colorScheme={selectedUser?.status ? 'green' : 'red'} fontSize="sm">
                        {selectedUser?.status ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Flex>
                  </Stack>
                </Box>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onAnalyticsClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserAccessManagementPage;
